import PropTypes from 'prop-types';
import React, { Component } from 'react';
import merge from 'lodash';

const SPINNER_ON = 'phase_on';
const SPINNER_OFF = 'phase_off';
const SPINNER_SHOWING = 'phase_showing';
const SPINNER_HIDING = 'phase_hiding';
const SPINNER_BEFORE_HIDING = 'phase_before_hiding';
const ANIMATION_TIME = 250; //milliseconds
const MIN_HEIGHT = 50;

export default class Spinner extends Component {
    static propTypes = {
        isLoading: PropTypes.bool,
        withAnimation: PropTypes.bool,
        tiny: PropTypes.bool,
        className: PropTypes.string
    };

    static defaultProps = {
        isLoading: false,
        withAnimation: false,
        className: "",
        tiny: false
    }

    constructor(props) {
        super(props);

        this.state = {
            initialSize: { w: null, h: null },
            loadedSize: { w: null, h: null },
            config: {
                animationTime: ANIMATION_TIME,
                minHeight: MIN_HEIGHT
            },
            isActive: false,
            phase: SPINNER_OFF
        }
        this.component = null;
        this.timeoutHandler = null
    }

    componentWillUpdate(nextProps) {
        if (!this.props.isLoading && nextProps.isLoading) {
            this.turnOn();
        } else if (this.props.isLoading && !nextProps.isLoading) {
            this.turnOff();
        }
    }

    componentDidMount() {
        if (this.props.isLoading) {
            this.turnOn();
        }
    }

    clearTimeoutHandler() {
        if (this.timeoutHandler) {
            clearTimeout(this.timeoutHandler);
        }
    }

    animationTimeout(callback) {
        this.clearTimeoutHandler();
        this.timeoutHandler = setTimeout(callback, this.state.config.animationTime);
    }

    turnOn(config = null) {
        if (config) {
            this.setState({ config: merge({}, this.state.config, config) })
        }
        this.setPhase(SPINNER_SHOWING);
    }

    turnOff(config) {
        if (config) {
            this.setState({ config: merge({}, this.state.config, config) })
        }
        this.clearTimeoutHandler();
        if (this.props.withAnimation) {
            this.setPhase(SPINNER_BEFORE_HIDING);
        } else {
            this.setPhase(SPINNER_OFF);
        }
    }

    setPhase(phase, callback) {
        switch(phase) {
            case SPINNER_SHOWING:
                this.setState({ phase }, () => {
                    this.setState({initialSize: this.getComponentSize()}, () => {
                        this.setPhase(SPINNER_ON)
                    });
                });
                break;
            case SPINNER_ON:
                this.setState({ phase });
                break;
            case SPINNER_BEFORE_HIDING:
                this.setState({ loadedSize: this.getComponentSize()}, () => {
                    this.setState({ phase }, () => {
                        this.setPhase(SPINNER_HIDING);
                    });
                });
                break;
            case SPINNER_HIDING:
                this.setState({ loadedSize: this.getComponentSize() }, () => {
                    this.setState({ phase }, () => {
                        this.animationTimeout(() => {
                            this.setPhase(SPINNER_OFF);
                        });
                    })
                });
                break;
            case SPINNER_OFF:
                this.setState({ phase });
                break;
            default:
                this.setState({ phase: SPINNER_ON });
        }
    }

    getComponentSize() {
        return { w: this.component.offsetWidth, h: this.component.offsetHeight }
    }

    getWrapperStyle() {
        if (this.props.withAnimation) {
            switch(this.state.phase) {
                case SPINNER_ON:
                    return {
                        width: this.state.initialSize.w,
                        height: this.state.initialSize.h > 0 ? this.state.initialSize.h : this.state.config.minHeight,
                        overflow: 'hidden',
                        transition: `all ${this.state.config.animationTime}ms ease-out`
                    };
                case SPINNER_BEFORE_HIDING:
                    return {
                        width: this.state.initialSize.w,
                        height: this.state.initialSize.h > 0 ? this.state.initialSize.h : this.state.config.minHeight,
                        transition: `all ${this.state.config.animationTime}ms ease-out`
                    };
                case SPINNER_HIDING:
                    return {
                        width: this.state.loadedSize.w,
                        height: this.state.loadedSize.h,
                        overflow: 'hidden',
                        transition: `all ${this.state.config.animationTime}ms ease-out`
                    };
                default:
                    return {
                        width: this.state.loadedSize.w,
                        height: this.state.loadedSize.h,
                        overflow: 'hidden',
                        transition: `all ${this.state.config.animationTime}ms ease-out`
                    }
            }
        } else {
            return {
                width: this.state.initialSize.w,
                height: this.state.initialSize.h > 0 ? this.state.initialSize.h : this.state.config.minHeight,
                overflow: 'hidden',
            }
        }
    }

    getOverlayStyle() {
        switch(this.state.phase) {
            case SPINNER_SHOWING:
                return {};
            case SPINNER_ON:
            case SPINNER_BEFORE_HIDING:
            case SPINNER_HIDING:
                return {
                    transition: `opacity ${this.state.config.animationTime}ms ease-out`,
                    //"transition-delay": `${this.state.config.animationTime}ms`
                };
            default:
                return {};
        }
    }

    getOverlayClass() {
        switch(this.state.phase) {
            case SPINNER_ON:
            case SPINNER_BEFORE_HIDING:
                return 'on';
            case SPINNER_HIDING:
                return 'off';
            default:
                return 'on';
        }
    }

    render() {
        switch(this.state.phase) {
            case SPINNER_SHOWING:
            case SPINNER_ON:
            case SPINNER_BEFORE_HIDING:
            case SPINNER_HIDING:
                return <div ref={(el) => this.wrapper = el}
                            style={this.getWrapperStyle()}
                            className={(this.props.tiny ? "tiny" : "") + " spinnerX " + this.props.className}>
                        <div className={'loader-overlay ' + this.getOverlayClass()} style={this.getOverlayStyle()}/>
                        <div ref={(el) => this.component = el}>
                            { this.props.children ? this.props.children : null }
                        </div>
                    </div>;
            case SPINNER_OFF:
                return this.props.children ? this.props.children : null;
            default:
                return <div ref={(el) => this.wrapper = el}
                            style={this.getWrapperStyle()}
                            className={(this.props.tiny ? "tiny" : "") + " spinnerX " + this.props.className}>
                    <div className={'loader-overlay ' + this.getOverlayClass()} style={this.getOverlayStyle()}/>
                    <div ref={(el) => this.component = el}>
                        { this.props.children ? this.props.children : null }
                    </div>
                </div>;
        }
    }
}


// WEBPACK FOOTER //
// ./src/scripts/reusable/Spinner/Spinner.jsx